import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;
  if (user === null) return <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-login-loading" />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border-b border-white/15 bg-transparent py-3 text-sm text-white placeholder:text-neutral-600 outline-none transition-colors duration-300 focus:border-brand";

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#0A0A0A] px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <Link to="/" data-testid="login-back-home" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 transition-colors hover:text-brand">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>
        <div className="mt-8 flex items-center gap-3">
          <img src="/logo-mark.png" alt="Catalyst Petrochem logo" className="h-9 w-auto" />
          <span className="font-display text-lg font-bold uppercase tracking-tight">
            Catalyst <span className="text-brand">Petrochem</span>
          </span>
        </div>
        <h1 className="mt-8 font-display text-4xl font-bold uppercase leading-[1.08] tracking-normal" data-testid="login-title">
          CMS Access
        </h1>
        <p className="mt-3 text-sm text-neutral-500">Authorised personnel only.</p>
        <form onSubmit={submit} className="mt-10 space-y-8" data-testid="login-form">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500" htmlFor="login-email">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
              placeholder="admin@catalystpetrochemllp.com"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500" htmlFor="login-password">
              Password
            </label>
            <input
              id="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
              placeholder="••••••••"
              data-testid="login-password-input"
            />
          </div>
          {error && (
            <p className="border border-brand/40 bg-brand/10 px-4 py-3 text-sm text-brand-light" data-testid="login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full bg-brand py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-deep disabled:opacity-50"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </main>
  );
}
